/**
 * Multi-column filter matching + visible-id collection (matches + ancestors).
 */
import type { TaskSnapshot } from '../stores/types';
import { addTaskIdWithAncestors } from './criticalPathFilter';
import type {
  ColumnFilterValues,
  GanttColumnDef,
  GanttColumnFilterValue,
} from './columns/types';

function isFilterActive(value: GanttColumnFilterValue | undefined): boolean {
  if (!value) return false;
  switch (value.type) {
    case 'text':
      return value.value.trim() !== '';
    case 'select':
      return value.values.length > 0;
    case 'boolean':
      return value.values.length > 0;
    case 'dateRange':
      return !!(value.from || value.to);
    case 'numberRange':
      return value.min != null || value.max != null;
    default:
      return false;
  }
}

export function hasActiveColumnFilters(values: ColumnFilterValues): boolean {
  return Object.values(values).some((v) => isFilterActive(v));
}

function normalizeDateKey(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw.toISOString().slice(0, 10);
  }
  const s = String(raw).trim();
  if (!s) return null;
  // Already YYYY-MM-DD or ISO
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function matchesOne(
  cell: string | number | boolean | null | undefined,
  filter: GanttColumnFilterValue,
): boolean {
  switch (filter.type) {
    case 'text': {
      const q = filter.value.trim().toLowerCase();
      if (!q) return true;
      return String(cell ?? '')
        .toLowerCase()
        .includes(q);
    }
    case 'select': {
      if (filter.values.length === 0) return true;
      return filter.values.includes(String(cell ?? ''));
    }
    case 'boolean': {
      if (filter.values.length === 0) return true;
      return filter.values.includes(Boolean(cell));
    }
    case 'dateRange': {
      const key = normalizeDateKey(cell);
      if (!key) return false;
      if (filter.from && key < filter.from) return false;
      if (filter.to && key > filter.to) return false;
      return true;
    }
    case 'numberRange': {
      const n = Number(cell);
      if (!Number.isFinite(n)) return false;
      if (filter.min != null && n < filter.min) return false;
      if (filter.max != null && n > filter.max) return false;
      return true;
    }
    default:
      return true;
  }
}

export type ColumnFilterMatchContext = {
  /** Optional live progress 0–1, critical flag, and tracked flag from DHTMLX / page. */
  getLiveMeta?: (
    taskId: string,
  ) => { progress?: number; critical?: boolean; tracked?: boolean } | undefined;
  /** Assignee user ids by task id (multi-select OR match). */
  assigneeUserIdsByTaskId?: Map<string, string[]>;
  /** Predecessor Sr Nos by task id (exact-match filter). */
  predecessorsSrNosByTaskId?: Map<string, string[]>;
  /** Location display name override (when locationType empty). */
  locationNameById?: Map<string, string>;
};

export function taskMatchesColumnFilters(
  task: TaskSnapshot,
  values: ColumnFilterValues,
  defs: GanttColumnDef[],
  ctx?: ColumnFilterMatchContext,
): boolean {
  for (const def of defs) {
    if (!def.filter) continue;
    const filterVal = values[def.id];
    if (!isFilterActive(filterVal)) continue;

    if (def.id === 'srNo') {
      if (filterVal?.type !== 'text') return false;
      const q = filterVal.value.trim().toLowerCase();
      if (!q) continue;
      const sr = task.srNo != null ? String(task.srNo).trim().toLowerCase() : '';
      if (sr !== q) return false;
      continue;
    }

    if (def.id === 'predecessors') {
      if (filterVal?.type !== 'text') return false;
      const q = filterVal.value.trim();
      if (!q) continue;
      const srNos = ctx?.predecessorsSrNosByTaskId?.get(String(task.id)) ?? [];
      if (!srNos.some((sr) => sr === q)) return false;
      continue;
    }

    if (def.id === 'assignees') {
      if (filterVal?.type !== 'select') return false;
      if (filterVal.values.length === 0) continue;
      const userIds = ctx?.assigneeUserIdsByTaskId?.get(String(task.id)) ?? [];
      if (!filterVal.values.some((v) => userIds.includes(v))) return false;
      continue;
    }

    let cell: string | number | boolean | null | undefined;
    if (def.id === 'locationType') {
      const fromFilter = def.filter.getValue(task);
      if (fromFilter) cell = fromFilter;
      else {
        const loc = String(task.locationId ?? '').trim();
        cell = loc ? ctx?.locationNameById?.get(loc) ?? '' : '';
      }
    } else {
      const live = ctx?.getLiveMeta?.(String(task.id));
      cell = def.filter.getValue(task, live);
    }

    if (!matchesOne(cell, filterVal!)) return false;
  }
  return true;
}

export type ColumnFilterCollectResult = {
  /** Matches + ancestors — use for display filter. */
  visibleIds: Set<string>;
  /** Direct matches only (excludes ancestor-only rows). */
  matchCount: number;
};

/**
 * Matching tasks + ancestors for WBS context.
 * Empty / inactive filters → null (caller treats as “no column filter”).
 */
export function collectColumnFilterVisibleTaskIds(
  allTasks: TaskSnapshot[],
  values: ColumnFilterValues,
  defs: GanttColumnDef[],
  ctx?: ColumnFilterMatchContext,
): ColumnFilterCollectResult | null {
  if (!hasActiveColumnFilters(values)) return null;

  const byId = new Map(allTasks.map((t) => [String(t.id), t]));
  const visibleIds = new Set<string>();
  let matchCount = 0;

  for (const task of allTasks) {
    if (!taskMatchesColumnFilters(task, values, defs, ctx)) continue;
    matchCount += 1;
    addTaskIdWithAncestors(visibleIds, String(task.id), byId);
  }

  return { visibleIds, matchCount };
}

/** Column ids that currently have an active filter value. */
export function getActiveFilterColumnIds(
  values: ColumnFilterValues,
  defs: GanttColumnDef[],
): string[] {
  const ids: string[] = [];
  for (const def of defs) {
    if (!def.filter) continue;
    const v = values[def.id];
    if (v != null && hasActiveColumnFilters({ [def.id]: v })) ids.push(def.id);
  }
  return ids;
}

function resolveGridHeadColumnName(el: HTMLElement): string | null {
  const fromAttr = el.getAttribute('data-column-name');
  if (fromAttr) return fromAttr;
  for (const cls of el.classList) {
    if (cls === 'gantt_grid_head_cell' || !cls.startsWith('gantt_grid_head_')) continue;
    return cls.slice('gantt_grid_head_'.length);
  }
  return null;
}

/** Mark / clear filtered-column header indicators without rebuilding columns. */
export function syncFilteredColumnHeaders(
  root: ParentNode,
  activeColumnIds: ReadonlySet<string>,
): void {
  root.querySelectorAll('.gantt_grid_head_cell').forEach((node) => {
    const el = node as HTMLElement;
    const name = resolveGridHeadColumnName(el);
    if (!name) return;
    const on = activeColumnIds.has(name);
    if (on) {
      el.setAttribute('data-column-filtered', 'true');
      el.classList.add('gantt-column-filtered');
    } else {
      el.removeAttribute('data-column-filtered');
      el.classList.remove('gantt-column-filtered');
    }
  });
}
